import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { CatalogItem, Country, ShipmentIdentifier } from '@shared';
import { formValuePicker } from '@shared';

@Injectable({
  providedIn: 'root',
})
export class FormValuesService {
  private fields: FormlyFieldConfig[] = [];

  constructor(private translateService: TranslateService) {}

  public init(config: {
    fieldsConfig: FormlyFieldConfig[];
    initialize: {
      key: string;
      withValues: CatalogItem[] | ShipmentIdentifier[] | Country[];
      defaultValue?: any;
    }[];
    whenDone: (fields: FormlyFieldConfig[]) => void;
  }): void {
    this.fields = [...config.fieldsConfig];

    config.initialize.forEach((item) => {
      this.update(item);
    });

    config.whenDone(this.fields);
  }

  public updateInput(item: {
    key: string;
    values: string | number;
    fieldsConfig: FormlyFieldConfig[];
    whenDone: (fields: FormlyFieldConfig[]) => void;
  }): void {
    this.fields = item.fieldsConfig;

    this.findField(item.key, (field) => {
      if (field.formControl) {
        field.formControl.patchValue(item.values);
      }
    });

    item.whenDone(this.fields);
  }

  public update(item: {
    key: string;
    withValues: CatalogItem[] | ShipmentIdentifier[] | Country[];
    defaultValue?: any;
    hideWhen?: boolean | ((model: any, formState: any, field?: FormlyFieldConfig | undefined) => boolean);
    fieldsConfig?: FormlyFieldConfig[];
    whenDone?: (fields: FormlyFieldConfig[]) => void;
  }): void {
    if (item.fieldsConfig) {
      this.fields = item.fieldsConfig;
    }
    this.findField(item.key, (field) => {
      if (item.hideWhen) {
        field.hideExpression = item.hideWhen;
      }

      if (field.formControl) {
        field.formControl.reset();
      }
      switch (item.key) {
        case formValuePicker.inquirerCountry:
          this.inquiryCountry(item.withValues as CatalogItem[], item.key);
          break;
        case formValuePicker.businessUnit:
          this.businessUnit(item.withValues as CatalogItem[], item.key, item.defaultValue);
          break;
        case formValuePicker.state:
        case formValuePicker.stateAssistance:
        case formValuePicker.country:
          this.country(item.withValues as Country[], item.key, item.defaultValue);
          break;
        case formValuePicker.shipmentIdentifierValues:
          this.shipmentIdentifier(item.withValues as ShipmentIdentifier[], item.key, item.defaultValue);
          break;
        case formValuePicker.caseCategory:
          this.category(item.withValues as CatalogItem[], item.key, item.defaultValue);
          break;
        case formValuePicker.caseSubCategory:
          this.subCategory(item.withValues as CatalogItem[], item.key, item.defaultValue);
          break;
        case formValuePicker.contactLanguage:
        case formValuePicker.contactLanguageAssistance:
          this.language(item.withValues as CatalogItem[], item.key);
          break;
        default:
          this.genericCatalogItem(item.withValues as CatalogItem[], item.key, item.defaultValue);
          break;
      }
    });

    if (item.whenDone) {
      item.whenDone(this.fields);
    }
  }

  private defaultOptionForSelect(): any {
    return { label: this.translateService.instant('createCase.defaultSelectValue'), value: null };
  }

  private findField(key: string, callback: (field: FormlyFieldConfig) => any): void {
    this.fields.forEach((fieldSection) => {
      if (fieldSection.fieldGroup) {
        fieldSection.fieldGroup.find((field) => {
          if (field.key === key) {
            callback(field);
          }
        });
      }
    });
  }

  private inquiryCountry(values: CatalogItem[], key: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((country) => {
            return { label: country.label, value: country.apiName, id: country.id };
          }),
        ];
      }
    });
  }

  private businessUnit(values: CatalogItem[], key: string, defaultValue?: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((businessUnit) => {
            return { label: businessUnit.label, value: businessUnit.apiName, id: businessUnit.id };
          }),
        ];

        if (defaultValue) {
          field.formControl?.patchValue(defaultValue);
        }
      }
    });
  }

  private country(values: Country[], key: string, defaultValue?: any): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions['default'] = defaultValue;
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((country) => {
            return { label: country.label, value: country.apiName, id: country.id };
          }),
        ];

        if (field.key === 'state' || field.key === 'stateAssistance') {
          field.formControl?.patchValue(defaultValue);
        }
      }
    });
  }

  private shipmentIdentifier(values: ShipmentIdentifier[], key: string, defaultValue?: any): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions['fields']) {
        field.templateOptions['fields'].forEach((field: FormlyFieldConfig) => {
          if (field.templateOptions && field.key === formValuePicker.shipmentIdentifierOptions) {
            field.templateOptions['default'] = defaultValue;
            field.templateOptions.options = [
              this.defaultOptionForSelect(),
              ...values.map((val) => {
                return { label: val.label, value: val.apiName, id: val.id };
              }),
            ];
          }
        });
      }
    });
  }

  private language(values: CatalogItem[], key: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((item) => {
            return { label: item.label, value: item.name, id: item.id, hiddenValue: item.id };
          }),
        ];
      }
    });
  }

  private genericCatalogItem(values: CatalogItem[], key: string, defaultValue?: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((item) => {
            return { label: item.label, value: item.apiName, id: item.id };
          }),
        ];

        if (defaultValue) {
          field.formControl?.patchValue(defaultValue);
        }
      }
    });
  }

  private category(values: CatalogItem[], key: string, defaultValue?: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((category) => {
            return { label: category.label, value: category.apiName, id: category.id };
          }),
        ];

        if (defaultValue) {
          field.formControl?.patchValue(defaultValue);
        }
      }
    });
  }

  private subCategory(values: CatalogItem[], key: string, defaultValue?: string): void {
    this.findField(key, (field) => {
      if (field.templateOptions && field.templateOptions.options) {
        field.templateOptions.options = [
          this.defaultOptionForSelect(),
          ...values.map((subCategory) => {
            return { label: subCategory.label, value: subCategory.apiName, id: subCategory.id };
          }),
        ];

        if (defaultValue) {
          field.formControl?.patchValue(defaultValue);
        }
      }
    });
  }
}
